// shared/src/oracle/oracle-store.ts
// D1 store for the Data Oracle tables (migrations/0083_oracle_price.sql). Writer
// + reader + Row types + Zod live in this ONE module (migration-lockstep gate;
// mirrors news-items-store.ts). Structural D1 subset so shared/ stays free of
// @cloudflare/workers-types.
//
// Tables: oracle_sources (per-source config) · oracle_policy (per-type resolver
// knobs) · oracle_observations (raw candidates) · oracle_canonical (resolved +
// provenance). See docs/data-oracle-2026-07-13-design.md §4/§7.2.

import { z } from 'zod';
import type { Observation, OracleDataType } from './observation.js';
import { DEFAULT_PRICE_POLICY, type ConsensusPolicy, type SourceConfig } from './resolve-consensus.js';
import { DEFAULT_MACRO_POLICY, type AuthoritativePolicy } from './resolve-authoritative.js';

/** Reusable Zod for the oracle data_type union (mirrors the relaxed
 *  CHECK(data_type IN ('price','macro')) after migration 0087). */
const DataTypeSchema = z.enum(['price', 'macro']);
/** Resolver mode per data_type: 'consensus' (price) | 'authoritative' (macro). */
export type OracleResolverMode = 'consensus' | 'authoritative';

// ── Structural D1 subset (prepare → bind → first/all/run/batch) ──
export interface OracleD1 {
  prepare(sql: string): OracleD1Stmt;
  batch(statements: OracleD1Stmt[]): Promise<unknown[]>;
}
export interface OracleD1Stmt {
  bind(...values: unknown[]): OracleD1Stmt;
  first<T = unknown>(): Promise<T | null>;
  all<T = unknown>(): Promise<{ results: T[]; success: boolean }>;
  run(): Promise<unknown>;
}

// ── FNV-1a → deterministic ids (idempotent INSERT OR IGNORE under retry) ──
function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

// ═══════════════════════ oracle_sources ═══════════════════════
export interface OracleSourceRow {
  readonly source_id:       string;
  readonly data_type:       OracleDataType;
  readonly enabled:         number;       // 0 | 1
  readonly weight:          number;
  readonly tier:            'A' | 'B' | 'C';
  readonly poll_interval_s: number;
  readonly rate_limit_json: string | null;
  readonly staleness_max_s: number;
  readonly config_json:     string | null;
  readonly updated_at:      string;
  readonly updated_by:      string | null;
}
export const OracleSourceRowSchema = z.object({
  source_id:       z.string(),
  data_type:       DataTypeSchema,
  enabled:         z.number().int().min(0).max(1),
  weight:          z.number(),
  tier:            z.enum(['A', 'B', 'C']),
  poll_interval_s: z.number().int().positive(),
  rate_limit_json: z.string().nullable(),
  staleness_max_s: z.number().int().positive(),
  config_json:     z.string().nullable(),
  updated_at:      z.string(),
  updated_by:      z.string().nullable(),
});
const SOURCE_COLS = 'source_id, data_type, enabled, weight, tier, poll_interval_s, rate_limit_json, staleness_max_s, config_json, updated_at, updated_by';

export async function readSources(db: OracleD1, dataType: OracleDataType): Promise<OracleSourceRow[]> {
  const res = await db
    .prepare(`SELECT ${SOURCE_COLS} FROM oracle_sources WHERE data_type = ? ORDER BY source_id ASC`)
    .bind(dataType)
    .all<OracleSourceRow>();
  return res.results ?? [];
}

/** Upsert a source config (seed + UI edit). updated_at is caller-supplied ISO-Z.
 *
 *  `extraStmts` (optional): additional prepared statements to run in the SAME `db.batch`
 *  as this upsert — e.g. the config-history rows for a UI PATCH, so the audit trail can
 *  never land without (or drift from) the row it describes (idempotent-under-retry). When
 *  empty (the default — every non-PATCH caller), this is a plain single-statement `run()`. */
export async function upsertSource(
  db: OracleD1, row: OracleSourceRow, extraStmts: OracleD1Stmt[] = [],
): Promise<void> {
  const stmt = db.prepare(
    `INSERT INTO oracle_sources (${SOURCE_COLS}) VALUES (?,?,?,?,?,?,?,?,?,?,?)
     ON CONFLICT(source_id) DO UPDATE SET
       enabled=excluded.enabled, weight=excluded.weight, tier=excluded.tier,
       poll_interval_s=excluded.poll_interval_s, rate_limit_json=excluded.rate_limit_json,
       staleness_max_s=excluded.staleness_max_s, config_json=excluded.config_json,
       updated_at=excluded.updated_at, updated_by=excluded.updated_by`,
  ).bind(
    row.source_id, row.data_type, row.enabled, row.weight, row.tier, row.poll_interval_s,
    row.rate_limit_json, row.staleness_max_s, row.config_json, row.updated_at, row.updated_by,
  );
  if (extraStmts.length === 0) {
    await stmt.run();
  } else {
    await db.batch([...extraStmts, stmt]);
  }
}

/** Map an oracle_sources row → the resolver's SourceConfig. `tier` is threaded
 *  for the AUTHORITATIVE resolver (issuer selection); CONSENSUS ignores it. */
export function toSourceConfig(row: OracleSourceRow): SourceConfig {
  return { weight: row.weight, staleness_max_s: row.staleness_max_s, tier: row.tier };
}

// ═══════════════════════ oracle_policy ═══════════════════════
/** The class this policy governs. `'*'` = every class of this data_type — the general row.
 *  A class-specific row WINS over `'*'` (readPolicyRow resolves the precedence), which is what lets
 *  equity run AUTHORITATIVE while crypto keeps CONSENSUS off the same data_type. Migration 0100. */
/** Which asset class a policy row governs. `'*'` = every class of that data_type (the fallback a
 *  class with no row of its own resolves on).
 *
 *  ONLY the classes a producer actually READS belong here. 'macro' was in this union and was dead
 *  both ways — the macro fire reads its own data_type and its row is `('macro','*')`, and a
 *  `('price','macro')` row is meaningless. A value the schema accepts but no producer reads is a row
 *  the API writes, the dash renders, and the resolver ignores: accepted, displayed, ignored — the
 *  precise shape that had to be fixed in the mode fork. Adding a class here means wiring its reader
 *  in the SAME commit. */
export type OracleAssetClassScope = '*' | 'crypto' | 'equity';

export interface OraclePolicyRow {
  readonly data_type:             OracleDataType;
  readonly asset_class:           OracleAssetClassScope; // '*' = the general row for this data_type
  readonly mode:                  OracleResolverMode;   // 'consensus' | 'authoritative'
  readonly min_sources:           number;               // CONSENSUS quorum (unused by AUTHORITATIVE)
  readonly mad_k:                 number;               // CONSENSUS outlier k (unused by AUTHORITATIVE)
  readonly outlier_floor_pct:     number;               // CONSENSUS absolute floor (unused by AUTHORITATIVE)
  readonly freshness_half_life_s: number;               // CONSENSUS weight decay (unused by AUTHORITATIVE)
  readonly corroboration_tol_pct: number | null;        // AUTHORITATIVE corroboration tolerance (null for CONSENSUS)
  readonly policy_version:        string;
  readonly updated_at:            string;
  readonly updated_by:            string | null;
}
export const OraclePolicyRowSchema = z.object({
  data_type:             DataTypeSchema,
  asset_class:           z.enum(['*', 'crypto', 'equity']),
  mode:                  z.enum(['consensus', 'authoritative']),
  min_sources:           z.number().int().positive(),
  mad_k:                 z.number().nonnegative(),
  outlier_floor_pct:     z.number().nonnegative(),
  freshness_half_life_s: z.number().int().positive(),
  corroboration_tol_pct: z.number().nonnegative().nullable(),
  policy_version:        z.string(),
  updated_at:            z.string(),
  updated_by:            z.string().nullable(),
}).superRefine((row, ctx) => {
  // AUTHORITATIVE without a tolerance is the one shape that FAILS SILENTLY instead of loudly:
  // toAuthoritativePolicy() below treats a null tolerance as "row absent" and returns
  // DEFAULT_MACRO_POLICY — so an equity row saved this way would resolve equity at the MACRO
  // tolerance (1%, tuned for a monthly FRED mirror) while the dashboard still displayed the row the
  // operator thought they saved. Nothing would log; the number would just be wrong by 5x.
  // Rejecting the row is the only way the mode toggle can be a safe control.
  // Both live rows already satisfy this (MEASURED 2026-07-15: macro authoritative/0.01,
  // price consensus/null), so this forbids a new mistake rather than legalising an old one.
  if (row.mode === 'authoritative' && row.corroboration_tol_pct == null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['corroboration_tol_pct'],
      message: "mode 'authoritative' requires corroboration_tol_pct (null would silently resolve at the macro default)",
    });
  }
});
const POLICY_COLS = 'data_type, asset_class, mode, min_sources, mad_k, outlier_floor_pct, freshness_half_life_s, corroboration_tol_pct, policy_version, updated_at, updated_by';

/** The policy governing (dataType, assetClass), MOST-SPECIFIC FIRST.
 *
 *  An exact `asset_class` row wins; otherwise the `'*'` row for that data_type answers. That single
 *  rule is the whole per-class mechanism: equity gets its own AUTHORITATIVE row (migration 0100)
 *  while crypto and thin keep resolving off the shared `'*'` CONSENSUS row, untouched.
 *
 *  `assetClass` defaults to `'*'`, so every pre-0100 caller keeps its exact previous behaviour —
 *  it reads the general row, which is the row it always read.
 *
 *  ORDER BY, not two queries: one subrequest, and `asset_class = ?` sorts before `'*'` only because
 *  the CASE makes it, not by string luck. */
export async function readPolicyRow(
  db:         OracleD1,
  dataType:   OracleDataType,
  assetClass: OracleAssetClassScope = '*',
): Promise<OraclePolicyRow | null> {
  return db
    .prepare(
      `SELECT ${POLICY_COLS} FROM oracle_policy
        WHERE data_type = ? AND asset_class IN (?, '*')
        ORDER BY CASE WHEN asset_class = ? THEN 0 ELSE 1 END
        LIMIT 1`,
    )
    .bind(dataType, assetClass, assetClass)
    .first<OraclePolicyRow>();
}

/** EVERY policy row for a data_type, general row first, then classes alphabetically.
 *
 *  For the Ops-dash, which must show what is ACTUALLY resolving. `readPolicyRow` answers "which one
 *  policy governs class X" — the resolver's question. A dashboard that asks that and renders the
 *  single answer as "the price policy" reports the general row's CONSENSUS while equity is really
 *  resolving AUTHORITATIVE off its own row: the same one-source-wearing-a-quorum's-clothes problem
 *  migration 0100 removed from the resolver, reappearing in the display. So the dash reads all rows
 *  and renders one per class.
 *
 *  Ordering is explicit, not incidental: '*' sorts first because the CASE puts it there, so the
 *  general row heads the table regardless of how SQLite would collate the literals. */
export async function readPolicies(
  db:       OracleD1,
  dataType: OracleDataType,
): Promise<readonly OraclePolicyRow[]> {
  const res = await db
    .prepare(
      `SELECT ${POLICY_COLS} FROM oracle_policy
        WHERE data_type = ?
        ORDER BY CASE WHEN asset_class = '*' THEN 0 ELSE 1 END, asset_class`,
    )
    .bind(dataType)
    .all<OraclePolicyRow>();
  return res.results ?? [];
}

/** Resolve the active CONSENSUS policy, falling back to the in-code default with a
 *  LOUD caller-side log if the row is absent (feedback_no_fallbacks = logged, not
 *  silent). */
export function toConsensusPolicy(row: OraclePolicyRow | null): ConsensusPolicy {
  if (!row) return DEFAULT_PRICE_POLICY;
  return {
    min_sources:           row.min_sources,
    mad_k:                 row.mad_k,
    outlier_floor_pct:     row.outlier_floor_pct,
    freshness_half_life_s: row.freshness_half_life_s,
    policy_version:        row.policy_version,
  };
}

/** Resolve the active AUTHORITATIVE policy, falling back to the in-code default
 *  (LOUD caller-side log) if the row is absent or lacks corroboration_tol_pct. */
export function toAuthoritativePolicy(row: OraclePolicyRow | null): AuthoritativePolicy {
  if (!row || row.corroboration_tol_pct == null) return DEFAULT_MACRO_POLICY;
  return {
    corroboration_tol_pct: row.corroboration_tol_pct,
    policy_version:        row.policy_version,
  };
}

/** `extraStmts` (optional): see upsertSource — batches the config-history rows for a UI
 *  PATCH in the SAME `db.batch` as this upsert. Empty for every non-PATCH caller. */
export async function upsertPolicy(
  db: OracleD1, row: OraclePolicyRow, extraStmts: OracleD1Stmt[] = [],
): Promise<void> {
  // Conflict target is the FULL PK (data_type, asset_class) per migration 0100. With the old
  // single-column target this would treat ('price','equity') as conflicting with ('price','*') and
  // silently overwrite the general row — turning a per-class edit into a fleet-wide one.
  const stmt = db.prepare(
    `INSERT INTO oracle_policy (${POLICY_COLS}) VALUES (?,?,?,?,?,?,?,?,?,?,?)
     ON CONFLICT(data_type, asset_class) DO UPDATE SET
       mode=excluded.mode, min_sources=excluded.min_sources, mad_k=excluded.mad_k,
       outlier_floor_pct=excluded.outlier_floor_pct,
       freshness_half_life_s=excluded.freshness_half_life_s,
       corroboration_tol_pct=excluded.corroboration_tol_pct, policy_version=excluded.policy_version,
       updated_at=excluded.updated_at, updated_by=excluded.updated_by`,
  ).bind(
    row.data_type, row.asset_class, row.mode, row.min_sources, row.mad_k, row.outlier_floor_pct,
    row.freshness_half_life_s, row.corroboration_tol_pct, row.policy_version, row.updated_at, row.updated_by,
  );
  if (extraStmts.length === 0) {
    await stmt.run();
  } else {
    await db.batch([...extraStmts, stmt]);
  }
}

// ═══════════════════════ oracle_config_history ═══════════════════════
// Per-field edit history for oracle_sources / oracle_policy (migration 0107). See
// docs/oracle-params-ops-paradigm-2026-07-18-spec.md §C.1. ONE ROW PER CHANGED FIELD
// (operator decision 2026-07-18) — a Save that changes 3 knobs writes 3 rows. `note` is
// its own column (not folded into changed_by, unlike runtime_params_history).
export interface OracleConfigHistoryRow {
  readonly id:         number;
  readonly entity:     'source' | 'policy' | 'relation' | 'feed' | 'news_policy';
  readonly entity_id:  string;   // source: source_id · policy: `${data_type}:${asset_class}` · news_policy: scope
  readonly field:      string;   // column edited (weight, mode, min_sources, …)
  readonly old_value:  string | null;
  readonly new_value:  string;
  readonly note:       string | null;
  readonly changed_at: string;   // ISO-Z (C018)
  readonly changed_by: string | null;
}
export const OracleConfigHistoryRowSchema = z.object({
  id:         z.number().int(),
  entity:     z.enum(['source', 'policy', 'relation', 'feed', 'news_policy']),
  entity_id:  z.string(),
  field:      z.string(),
  old_value:  z.string().nullable(),
  new_value:  z.string(),
  note:       z.string().nullable(),
  changed_at: z.string(),
  changed_by: z.string().nullable(),
});
const CONFIG_HISTORY_COLS = 'id, entity, entity_id, field, old_value, new_value, note, changed_at, changed_by';

/** Build (but do not run) one bound INSERT statement per row — lets a caller (e.g. the
 *  oracle.ts PATCH routes) combine these with the entity's own upsert statement in ONE
 *  `db.batch`, so the history rows can never land without (or drift from) the row they
 *  describe. `insertOracleConfigHistory` below is the standalone convenience wrapper. */
export function oracleConfigHistoryInsertStmts(
  db: OracleD1, rows: ReadonlyArray<Omit<OracleConfigHistoryRow, 'id'>>,
): OracleD1Stmt[] {
  const stmt = db.prepare(
    `INSERT INTO oracle_config_history (entity, entity_id, field, old_value, new_value, note, changed_at, changed_by)
     VALUES (?,?,?,?,?,?,?,?)`,
  );
  return rows.map((r) => stmt.bind(
    r.entity, r.entity_id, r.field, r.old_value, r.new_value, r.note, r.changed_at, r.changed_by,
  ));
}

/** Batch-insert one row per changed field (caller computes the diff; a no-op PATCH
 *  passes an empty array and writes nothing). `id` is caller-omitted (AUTOINCREMENT).
 *  Standalone convenience wrapper — see `oracleConfigHistoryInsertStmts` to combine with
 *  another statement in the same `db.batch`. */
export async function insertOracleConfigHistory(
  db: OracleD1, rows: ReadonlyArray<Omit<OracleConfigHistoryRow, 'id'>>,
): Promise<void> {
  if (rows.length === 0) return;
  await db.batch(oracleConfigHistoryInsertStmts(db, rows));
}

/** Most-recent-first edit history for one entity instance — the Ops-dash History modal.
 *  `id DESC` is a secondary tiebreak: a multi-field Save writes all its rows with the SAME
 *  `changed_at` (one request, one timestamp), so `changed_at DESC` alone leaves same-Save rows
 *  in SQLite's unspecified tie order. `id` is monotonic insertion order, so this makes ties
 *  deterministic without changing the primary (across-Saves) ordering. */
export async function readOracleConfigHistory(
  db: OracleD1, entity: 'source' | 'policy' | 'relation' | 'feed' | 'news_policy', entityId: string, limit = 50,
): Promise<OracleConfigHistoryRow[]> {
  const res = await db
    .prepare(
      `SELECT ${CONFIG_HISTORY_COLS} FROM oracle_config_history
        WHERE entity = ? AND entity_id = ? ORDER BY changed_at DESC, id DESC LIMIT ?`,
    )
    .bind(entity, entityId, limit)
    .all<OracleConfigHistoryRow>();
  return res.results ?? [];
}

// ═══════════════════════ oracle_observations ═══════════════════════
export interface OracleObservationRow {
  readonly id:          string;
  readonly run_id:      string;
  readonly source_id:   string;
  readonly data_type:   OracleDataType;
  readonly key:         string;
  readonly value:       number | null;
  readonly observed_at: string | null;
  readonly fetched_at:  string;
  readonly quality:     string;   // JSON array
  readonly created_at:  string;
}
export const OracleObservationRowSchema = z.object({
  id:          z.string(),
  run_id:      z.string(),
  source_id:   z.string(),
  data_type:   DataTypeSchema,
  key:         z.string(),
  value:       z.number().nullable(),
  observed_at: z.string().nullable(),
  fetched_at:  z.string(),
  quality:     z.string(),
  created_at:  z.string(),
});
const OBS_COLS = 'id, run_id, source_id, data_type, key, value, observed_at, fetched_at, quality, created_at';

/** Deterministic id per (run_id, source_id, key) → a step retry re-inserts the
 *  SAME row (INSERT OR IGNORE), keying the LOGICAL observation not the attempt
 *  (feedback_idempotency_identity_excludes_run applies to logical item, run here
 *  IS the logical grouping so it is part of the key). */
export function makeObservationId(runId: string, sourceId: string, key: string): string {
  return `obs_${fnv1a(`${runId}|${sourceId}|${key}`)}${fnv1a(`${key}|${sourceId}|${runId}`)}`;
}

export function observationToRow(o: Observation, runId: string, createdAtIso: string): OracleObservationRow {
  return {
    id:          makeObservationId(runId, o.source_id, o.key),
    run_id:      runId,
    source_id:   o.source_id,
    data_type:   o.data_type,
    key:         o.key,
    value:       o.value,
    observed_at: o.observed_at,
    fetched_at:  o.fetched_at,
    quality:     JSON.stringify(o.quality),
    created_at:  createdAtIso,
  };
}

export async function writeObservations(db: OracleD1, rows: ReadonlyArray<OracleObservationRow>): Promise<void> {
  if (rows.length === 0) return;
  const stmt = db.prepare(`INSERT OR IGNORE INTO oracle_observations (${OBS_COLS}) VALUES (?,?,?,?,?,?,?,?,?,?)`);
  await db.batch(rows.map((r) => stmt.bind(
    r.id, r.run_id, r.source_id, r.data_type, r.key, r.value, r.observed_at, r.fetched_at, r.quality, r.created_at,
  )));
}

// ═══════════════════════ oracle_canonical ═══════════════════════
export interface OracleCanonicalRow {
  readonly id:              string;
  readonly run_id:          string;
  readonly data_type:       OracleDataType;
  readonly key:             string;
  readonly value:           number | null;
  readonly observed_at:     string | null;
  readonly status:          'ok' | 'below_quorum' | 'no_candidates';
  readonly provenance_json: string;
  readonly policy_version:  string;
  readonly created_at:      string;
}
export const OracleCanonicalRowSchema = z.object({
  id:              z.string(),
  run_id:          z.string(),
  data_type:       DataTypeSchema,
  key:             z.string(),
  value:           z.number().nullable(),
  observed_at:     z.string().nullable(),
  status:          z.enum(['ok', 'below_quorum', 'no_candidates']),
  provenance_json: z.string(),
  policy_version:  z.string(),
  created_at:      z.string(),
});
const CANON_COLS = 'id, run_id, data_type, key, value, observed_at, status, provenance_json, policy_version, created_at';

export function makeCanonicalId(runId: string, key: string): string {
  return `canon_${fnv1a(`${runId}|${key}`)}${fnv1a(`${key}|${runId}`)}`;
}

export async function writeCanonical(db: OracleD1, rows: ReadonlyArray<OracleCanonicalRow>): Promise<void> {
  if (rows.length === 0) return;
  const stmt = db.prepare(`INSERT OR IGNORE INTO oracle_canonical (${CANON_COLS}) VALUES (?,?,?,?,?,?,?,?,?,?)`);
  await db.batch(rows.map((r) => stmt.bind(
    r.id, r.run_id, r.data_type, r.key, r.value, r.observed_at, r.status, r.provenance_json, r.policy_version, r.created_at,
  )));
}

// REMOVED 2026-07-15: `readLatestCanonical(db, key)` — newest row for a key, blind on BOTH
// axes this table is partitioned by (no `value IS NOT NULL`, no `data_type`). It had ZERO
// callers and was exported from the barrel, which made it a loaded gun: its name is the one
// a future consumer would reach for first, and it would have handed them a fail-close null
// shadowing a live price, or a six-week-old FRED release value as a spot quote. Both are real
// bugs caught in review on this very path (2026-07-15). Use readLastGoodCanonical(db, key,
// dataType) for a single key, or the consumer-read layer for an age-gated read.

/** Latest canonical with a non-null value (the preserved last-good) for a key, WITHIN a
 *  data_type.
 *
 *  `dataType` is REQUIRED, not optional-with-a-default (added 2026-07-15). `oracle_canonical`
 *  holds price AND macro rows, and `makeCanonicalId` does not include data_type either — so a
 *  key-only read is scoped by nothing but the hope that the two key namespaces never collide.
 *  They happen not to today ($ETH/SPY vs FEDFUNDS/DGS10), but nothing ENFORCES that, and the
 *  two data types have incompatible age semantics: a price is stale after minutes, whereas a
 *  macro observed_at is the FRED RELEASE DATE and is legitimately weeks old. A data_type-blind
 *  read judged by a price freshness gate would call every healthy macro series stale — so the
 *  scope belongs in the query, where it cannot be forgotten, rather than in a caller's head. */
export async function readLastGoodCanonical(
  db: OracleD1, key: string, dataType: OracleDataType,
): Promise<OracleCanonicalRow | null> {
  return db
    .prepare(`SELECT ${CANON_COLS} FROM oracle_canonical WHERE key = ? AND data_type = ? AND value IS NOT NULL ORDER BY created_at DESC LIMIT 1`)
    .bind(key, dataType)
    .first<OracleCanonicalRow>();
}

// ═══════════════════════ oracle_source_health ═══════════════════════
// One (source,key) contribution to one CONSENSUS fire — the f_freshness trace (task #2, migration
// 0115). Both the decision-3 audit trail (a weighting that leaves no trace is indistinguishable
// from one that never ran) AND the decision-4 shadow log (the w_effective series IS the
// effective-weight-quorum evidence §5a defers on). CONSENSUS keys only (equity is AUTHORITATIVE).
export interface OracleSourceHealthRow {
  readonly id:           string;
  readonly run_id:       string;
  readonly data_type:    OracleDataType;
  readonly key:          string;
  readonly source_id:    string;
  readonly observed_at:  string | null;
  readonly w_configured: number;
  readonly f_age:        number;
  readonly f_freshness:  number;
  readonly w_effective:  number;
  readonly self_moved:   number;   // 0 | 1
  readonly peers_moved:  number;   // 0 | 1
  readonly motionless_s: number;
  readonly verdict:      'ok' | 'quiet' | 'frozen';
  readonly created_at:   string;
}
export const OracleSourceHealthRowSchema = z.object({
  id:           z.string(),
  run_id:       z.string(),
  data_type:    DataTypeSchema,
  key:          z.string(),
  source_id:    z.string(),
  observed_at:  z.string().nullable(),
  w_configured: z.number(),
  f_age:        z.number(),
  f_freshness:  z.number().min(0).max(1),
  w_effective:  z.number(),
  self_moved:   z.number().int().min(0).max(1),
  peers_moved:  z.number().int().min(0).max(1),
  motionless_s: z.number(),
  verdict:      z.enum(['ok', 'quiet', 'frozen']),
  created_at:   z.string(),
});
const SOURCE_HEALTH_COLS = 'id, run_id, data_type, key, source_id, observed_at, w_configured, f_age, f_freshness, w_effective, self_moved, peers_moved, motionless_s, verdict, created_at';

/** Deterministic id per (run_id, key, source_id) — a step retry re-inserts the SAME row
 *  (INSERT OR IGNORE), keying the LOGICAL health sample not the attempt. */
export function makeSourceHealthId(runId: string, key: string, sourceId: string): string {
  return `srch_${fnv1a(`${runId}|${key}|${sourceId}`)}${fnv1a(`${sourceId}|${key}|${runId}`)}`;
}

export async function writeSourceHealth(db: OracleD1, rows: ReadonlyArray<OracleSourceHealthRow>): Promise<void> {
  if (rows.length === 0) return;
  const stmt = db.prepare(`INSERT OR IGNORE INTO oracle_source_health (${SOURCE_HEALTH_COLS}) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`);
  await db.batch(rows.map((r) => stmt.bind(
    r.id, r.run_id, r.data_type, r.key, r.source_id, r.observed_at, r.w_configured, r.f_age,
    r.f_freshness, r.w_effective, r.self_moved, r.peers_moved, r.motionless_s, r.verdict, r.created_at,
  )));
}

/** Recent health rows for a (key, source_id) series — the "is this source getting worse?" read the
 *  Consensus/#98-lineage view uses. Newest first. `sinceIso` is an ISO-Z cutoff (C018). */
export async function readSourceHealthWindow(
  db: OracleD1, key: string, sinceIso: string,
): Promise<OracleSourceHealthRow[]> {
  const res = await db
    .prepare(`SELECT ${SOURCE_HEALTH_COLS} FROM oracle_source_health WHERE key = ? AND created_at > ? ORDER BY created_at DESC`)
    .bind(key, sinceIso)
    .all<OracleSourceHealthRow>();
  return res.results ?? [];
}

// ═══════════════════════ oracle_macro_block ═══════════════════════
// The full 30-field FRED macro block (JSON) produced by the macro fire, so the
// consumer (refresh) reads the whole computed.macro FRED sub-block from the oracle
// instead of fetching FRED itself (universal-source flip §3, P1b). One row per fire.
export interface OracleMacroBlockRow {
  readonly id:         string;
  readonly run_id:     string;
  readonly block_json: string;   // JSON.stringify(buildFredMacroBlock(seriesMap))
  readonly created_at: string;
}
export const OracleMacroBlockRowSchema = z.object({
  id:         z.string(),
  run_id:     z.string(),
  block_json: z.string(),
  created_at: z.string(),
});
const MACRO_BLOCK_COLS = 'id, run_id, block_json, created_at';

export async function writeMacroBlock(db: OracleD1, row: OracleMacroBlockRow): Promise<void> {
  await db
    .prepare(`INSERT OR IGNORE INTO oracle_macro_block (${MACRO_BLOCK_COLS}) VALUES (?,?,?,?)`)
    .bind(row.id, row.run_id, row.block_json, row.created_at)
    .run();
}

/** Latest macro block — the consumer read path (refresh reads this into computed.macro). */
export async function readLatestMacroBlock(db: OracleD1): Promise<OracleMacroBlockRow | null> {
  return db
    .prepare(`SELECT ${MACRO_BLOCK_COLS} FROM oracle_macro_block ORDER BY created_at DESC LIMIT 1`)
    .first<OracleMacroBlockRow>();
}

/** Rows for the Runs list: the `runLimit` most-recent resolve cycles UNION each key's
 *  OWN latest cycle. A plain last-N-rows window is flooded by a fast-cadence class — crypto
 *  fires every ~5 min while equity/commodity resolve on a multi-day cadence, so the recent
 *  window is 100% crypto and the slower keys' runs are evicted entirely (the Runs tab showed
 *  only $BTC/$ETH). The per-key-latest arm guarantees every key's most-recent resolve cycle
 *  appears as a run, regardless of cadence; the recent-N arm preserves the fast class's run
 *  history. Downstream groups these rows by run_id.
 *
 *  ─── D1 rows-read reduction (migration 0130) ──────────────────────────────────────────────
 *  Both arms of the old query (`GROUP BY run_id ORDER BY MAX(created_at)` + `ROW_NUMBER() … rn=1`)
 *  scanned the whole partition with temp b-trees, and the outer `run_id IN (…)` re-scanned it once
 *  more (no run_id index). Rewritten as bounded indexed reads keyed on `created_at` — safe because
 *  every row of a fire shares ONE `created_at` (the fire's nowIso; oracle-worker writes all keys
 *  with the same createdAtIso), so a run is fully identified by its (run_id, created_at):
 *    · recent-N arm  → the runLimit newest DISTINCT created_at values (skip-scan on
 *      idx_oracle_canon_dt_created), then a bounded range fetch `created_at >= floor`.
 *    · per-key-latest arm → readLatestCanonicalPerKey (O(#keys) seeks) gives each key's newest
 *      row; for its runs that fall BELOW the recent floor, a `created_at IN (…) AND run_id IN (…)`
 *      seek fetches their full membership (all keys that resolved in those runs).
 *  Union deduped by id. No temp b-tree, no full partition scan. Result set identical to the old
 *  query's (recent-N runs ∪ each key's latest run). */
export async function readRunsCanonical(
  db: OracleD1, dataType: OracleDataType, runLimit: number,
): Promise<OracleCanonicalRow[]> {
  // recent-N-cycles floor: the runLimit-th newest distinct created_at (a fire = one created_at).
  const floorRes = await db
    .prepare(
      `SELECT DISTINCT created_at FROM oracle_canonical WHERE data_type = ? ORDER BY created_at DESC LIMIT ?`
    )
    .bind(dataType, runLimit)
    .all<{ created_at: string }>();
  const floors = (floorRes.results ?? []).map((r) => r.created_at);
  const recentFloor = floors.length > 0 ? floors[floors.length - 1]! : null;

  const byId = new Map<string, OracleCanonicalRow>();

  // recent-N arm: every row at-or-after the floor (the recent cycles), newest first.
  if (recentFloor !== null) {
    const recentRes = await db
      .prepare(
        `SELECT ${CANON_COLS} FROM oracle_canonical WHERE data_type = ? AND created_at >= ? ORDER BY created_at DESC`
      )
      .bind(dataType, recentFloor)
      .all<OracleCanonicalRow>();
    for (const r of recentRes.results ?? []) byId.set(r.id, r);
  }

  // per-key-latest arm: each key's newest row (O(#keys) seeks). For its runs BELOW the recent
  // floor, fetch the full run membership by (created_at IN, run_id IN) — all rows of a run share
  // its created_at, so this collects every key that resolved in those runs.
  const perKeyLatest = await readLatestCanonicalPerKey(db, dataType);
  const belowCreatedAt = new Set<string>();
  const belowRunIds = new Set<string>();
  for (const r of perKeyLatest) {
    if (recentFloor === null || r.created_at < recentFloor) {
      belowCreatedAt.add(r.created_at);
      belowRunIds.add(r.run_id);
    }
  }
  if (belowCreatedAt.size > 0) {
    const cats = [...belowCreatedAt];
    const rids = [...belowRunIds];
    const catPh = cats.map(() => '?').join(',');
    const ridPh = rids.map(() => '?').join(',');
    const bRes = await db
      .prepare(
        `SELECT ${CANON_COLS} FROM oracle_canonical`
        + ` WHERE data_type = ? AND created_at IN (${catPh}) AND run_id IN (${ridPh})`
      )
      .bind(dataType, ...cats, ...rids)
      .all<OracleCanonicalRow>();
    for (const r of bRes.results ?? []) byId.set(r.id, r);
  }

  return [...byId.values()].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

/** Latest canonical row PER KEY across the whole universe — the Ops-dash CONSENSUS
 *  view source. Unlike a last-N-ROWS window (which a fast-cadence class floods, hiding
 *  slower-cadence keys), this returns exactly one row per key (its newest), so EVERY key
 *  that has ever resolved shows — crypto (5-min) and the slower/dormant equity+commodity
 *  keys alike. Freshness is surfaced per-row (age), so a stale key is visible, never dropped. */
export async function readLatestCanonicalPerKey(
  db: OracleD1, dataType: OracleDataType,
): Promise<OracleCanonicalRow[]> {
  // Migration 0132 (completes 0130): phase-1 key enumeration was `SELECT DISTINCT key … WHERE
  // data_type=?`, which SQLite plans as a FULL scan of the whole data_type partition (~61k index
  // entries) just to derive ~55 keys — the planner will not auto-convert DISTINCT into a key-jumping
  // scan. Rewritten as a recursive "loose (skip) index scan" that seeks key-to-key on
  // idx_oracle_canon_dtk (data_type, key, created_at): seed MIN(key), then repeatedly the MIN(key)
  // with key > prev. O(#keys) index seeks for the enumeration, ~55 not ~61k. The trailing NULL row
  // (the terminal MIN(key) that finds no larger key) is filtered by `WHERE dk.key IS NOT NULL`, so
  // the distinct-key SET is byte-identical to `SELECT DISTINCT key` — INCLUDING the DE-LISTED tail
  // (no fixed key list, so a key no longer in the live universe is still enumerated). Phase 2 is
  // unchanged: each key's newest row via `ORDER BY created_at DESC LIMIT 1` (NO value filter — this
  // reader wants newest regardless of null, per its docstring).
  const res = await db
    .prepare(
      `WITH RECURSIVE dk(key) AS (`
      + `SELECT (SELECT MIN(key) FROM oracle_canonical WHERE data_type = ?)`
      + ` UNION ALL`
      + ` SELECT (SELECT MIN(key) FROM oracle_canonical WHERE data_type = ? AND key > dk.key)`
      + ` FROM dk WHERE dk.key IS NOT NULL`
      + `) `
      + `SELECT ${CANON_COLS} FROM oracle_canonical c WHERE c.id IN (`
      + `SELECT (SELECT c2.id FROM oracle_canonical c2`
      + ` WHERE c2.data_type = ? AND c2.key = dk.key`
      + ` ORDER BY c2.created_at DESC LIMIT 1)`
      + ` FROM dk WHERE dk.key IS NOT NULL)`
    )
    .bind(dataType, dataType, dataType)
    .all<OracleCanonicalRow>();
  return res.results ?? [];
}

/** Newest NON-NULL canonical per key — the batch analogue of readLastGoodCanonical.
 *
 *  Distinct from readLatestCanonicalPerKey above, and the difference is load-bearing: that
 *  one returns each key's newest row REGARDLESS of value, which is right for a dashboard
 *  (a fail-close is a thing you want to SEE). It is wrong for a consumer read, because the
 *  resolver persists `value: null` on every no_candidates/below_quorum fire — so one
 *  fail-close row SHADOWS a perfectly good value from five minutes earlier, and the consumer
 *  concludes the key is absent when its last-good is sitting right there. That would silently
 *  defeat the preserve-last-good invariant (§7b) for exactly the transient blips it exists
 *  to ride out.
 *
 *  One row per key, newest-first among rows that actually carry a value.
 *
 *  ─── D1 rows-read reduction (migration 0130) ──────────────────────────────────────────────
 *  `keys` is the EXPLICIT universe list (already in hand at readFreshCanonicalBatch — this reader
 *  is only ever fed a bounded key set). The old `ROW_NUMBER() OVER (PARTITION BY key …)` windowed
 *  the WHOLE `data_type` partition (~63k rows) + a TEMP B-TREE sort on EVERY fire. The rewrite is a
 *  per-key correlated `id = (… ORDER BY created_at DESC LIMIT 1)` seek on `idx_oracle_canon_dtk`
 *  (data_type, key, created_at): ONE index seek per key ⇒ O(#keys) rows read, no temp b-tree.
 *  Semantics preserved byte-for-byte: `value IS NOT NULL` last-good skip (a fail-close null never
 *  shadows), `data_type` isolation, `created_at` tie-break (LIMIT 1 returns exactly one row per key,
 *  matching ROW_NUMBER rn=1 — proven equivalent incl. a shared-`created_at` tie in the fence test).
 *  Empty `keys` ⇒ [] without touching D1. */
export async function readLastGoodCanonicalPerKey(
  db: OracleD1, dataType: OracleDataType, keys: ReadonlyArray<string>,
): Promise<OracleCanonicalRow[]> {
  if (keys.length === 0) return [];
  const ks = keys.map(() => '(?)').join(',');
  const res = await db
    .prepare(
      `WITH ks(k) AS (VALUES ${ks}) `
      + `SELECT ${CANON_COLS} FROM oracle_canonical c WHERE c.id IN (`
      + `SELECT (SELECT c2.id FROM oracle_canonical c2`
      + ` WHERE c2.data_type = ? AND c2.key = ks.k AND c2.value IS NOT NULL`
      + ` ORDER BY c2.created_at DESC LIMIT 1) FROM ks)`
    )
    .bind(...keys, dataType)
    .all<OracleCanonicalRow>();
  return res.results ?? [];
}

/** As readLastGoodCanonicalPerKey, but PINNED to an as-of instant: the last-good row per key with
 *  `created_at <= asOfIso`. This is the honest `run`-clock read (Slice 5 / #64) — it answers "what
 *  did the oracle know AS OF T_run?", not "what is newest now". Same fail-close-NULL skip
 *  (`value IS NOT NULL`) and same DESC-ranked last-good semantics as the un-pinned reader, so a
 *  blip never shadows a good value; only the `created_at <= ?` bound differs. `asOfIso` is an ISO-Z
 *  instant (C018: created_at is ISO-`T…Z`, so a string compare is a time compare).
 *
 *  Same migration-0130 correlated-seek rewrite as readLastGoodCanonicalPerKey, with the `created_at
 *  <= ?` as-of pin threaded into the per-key seek's index range (data_type, key, created_at < asOf). */
export async function readLastGoodCanonicalPerKeyAsOf(
  db: OracleD1, dataType: OracleDataType, asOfIso: string, keys: ReadonlyArray<string>,
): Promise<OracleCanonicalRow[]> {
  if (keys.length === 0) return [];
  const ks = keys.map(() => '(?)').join(',');
  const res = await db
    .prepare(
      `WITH ks(k) AS (VALUES ${ks}) `
      + `SELECT ${CANON_COLS} FROM oracle_canonical c WHERE c.id IN (`
      + `SELECT (SELECT c2.id FROM oracle_canonical c2`
      + ` WHERE c2.data_type = ? AND c2.key = ks.k AND c2.value IS NOT NULL AND c2.created_at <= ?`
      + ` ORDER BY c2.created_at DESC LIMIT 1) FROM ks)`
    )
    .bind(...keys, dataType, asOfIso)
    .all<OracleCanonicalRow>();
  return res.results ?? [];
}

/** The PREVIOUS resolved value per key (the 2nd-newest canonical row) — powers the
 *  Consensus "Δ vs prev run" column (current resolved − previous resolved). One row
 *  per key or absent (a key with a single resolve has no prior).
 *
 *  Migration 0132 (completes 0130): phase-1 enumeration was `SELECT DISTINCT key … WHERE
 *  data_type=?`, a full data_type-partition scan (~61k entries) to derive ~55 keys — same residual
 *  as readLatestCanonicalPerKey. Rewritten as the SAME recursive loose-index-scan (seed MIN(key),
 *  then MIN(key) with key > prev, seeking key-to-key on idx_oracle_canon_dtk), so the enumeration is
 *  O(#keys) not O(partition). The trailing terminal NULL is filtered by `WHERE dk.key IS NOT NULL`,
 *  leaving a distinct-key set byte-identical to `SELECT DISTINCT key`. Phase 2 is unchanged: per key
 *  the 2nd row in (created_at DESC) order via `LIMIT 1 OFFSET 1`. NO value filter (matches the old
 *  reader — a fail-close prev is a real prior value to show). The `prev_id` is selected alongside
 *  `value` so a 2nd row that carries a NULL value (present → key in the Map with null) is kept
 *  DISTINCT from a key with no 2nd row at all (absent → not in the Map), exactly as `rn=2` did. */
export async function readPrevValueByKey(
  db: OracleD1, dataType: OracleDataType,
): Promise<Map<string, number | null>> {
  const res = await db
    .prepare(
      `WITH RECURSIVE dk(key) AS (`
      + `SELECT (SELECT MIN(key) FROM oracle_canonical WHERE data_type = ?)`
      + ` UNION ALL`
      + ` SELECT (SELECT MIN(key) FROM oracle_canonical WHERE data_type = ? AND key > dk.key)`
      + ` FROM dk WHERE dk.key IS NOT NULL`
      + `) `
      + `SELECT c.key AS key, c.value AS value FROM oracle_canonical c WHERE c.id IN (`
      + `SELECT (SELECT c2.id FROM oracle_canonical c2`
      + ` WHERE c2.data_type = ? AND c2.key = dk.key`
      + ` ORDER BY c2.created_at DESC LIMIT 1 OFFSET 1)`
      + ` FROM dk WHERE dk.key IS NOT NULL)`
    )
    .bind(dataType, dataType, dataType)
    .all<{ key: string; value: number | null }>();
  return new Map((res.results ?? []).map((r) => [r.key, r.value]));
}

/** All observations in a recent window — the Ops-dash Health/Cost aggregate +
 *  the raw-vs-resolved compare (projector filters to the latest run per key).
 *  `sinceIso` is an ISO-Z cutoff (C018: created_at is ISO-`T…Z`). */
export async function readObservationsWindow(
  db: OracleD1, dataType: OracleDataType, sinceIso: string,
): Promise<OracleObservationRow[]> {
  const res = await db
    .prepare(`SELECT ${OBS_COLS} FROM oracle_observations WHERE data_type = ? AND created_at > ? ORDER BY created_at DESC`)
    .bind(dataType, sinceIso)
    .all<OracleObservationRow>();
  return res.results ?? [];
}
